import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Vector3, type Group } from 'three';
import { useShallow } from 'zustand/shallow';

import { SettingModelSettingType } from 'api/models';
import { PreviewInstance } from 'app/modules/wallforming/components/scenes/objects/placement-preview/PreviewInstance';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface PlacementPreviewProps {
    isMainCanvas: boolean;
    centered?: boolean;
    onBoundsReady?: (group: Group) => void;
    onLoadingChange?: (isLoading: boolean) => void;
}

export const PlacementPreview = memo(
    ({ isMainCanvas, centered = false, onBoundsReady, onLoadingChange }: PlacementPreviewProps) => {
        const groupRef = useRef<Group>(null);
        const loadedPartsRef = useRef({ solutionKey: '', count: 0 });

        const { solution, showPlacementPreview, selectedSettingType, selectedSettingView, automatedPlacementTab } =
            usePlacementStore(
                useShallow((state) => {
                    const selectedIndex = state.selectedPlacementSolution;
                    const solution = selectedIndex === null ? null : (state.placementSolutions[selectedIndex] ?? null);
                    return {
                        solution,
                        showPlacementPreview: state.showPlacementPreview,
                        selectedSettingType: state.selectedSettingType,
                        selectedSettingView: state.selectedSettingView,
                        automatedPlacementTab: state.automatedPlacementTab
                    };
                })
            );

        const isPlacementPreviewContext = useMemo(
            () =>
                solution !== null &&
                selectedSettingType === SettingModelSettingType.Corners &&
                selectedSettingView === SettingView.AUTOMATED_PLACEMENT &&
                automatedPlacementTab === PlacementTab.SOLUTIONS,
            [automatedPlacementTab, selectedSettingType, selectedSettingView, solution]
        );

        const isVisible = useMemo(
            () => isPlacementPreviewContext && (!isMainCanvas || showPlacementPreview),
            [isMainCanvas, isPlacementPreviewContext, showPlacementPreview]
        );

        const solutionKey = useMemo(
            () =>
                solution
                    ? solution.placements
                          .map((group) => group.instances.map((instance) => instance.id).join(','))
                          .join('|')
                    : '',
            [solution]
        );

        const localCenter = useMemo(() => {
            if (!centered || !solution) {
                return new Vector3();
            }

            const positions = solution.placements.flatMap((group) =>
                group.instances.flatMap((instance) => instance.parts.map((part) => part.position))
            );
            if (positions.length === 0) {
                return new Vector3();
            }

            const min = new Vector3(Infinity, Infinity, Infinity);
            const max = new Vector3(-Infinity, -Infinity, -Infinity);
            positions.forEach((position) => {
                min.min(new Vector3(position.x, position.y, position.z));
                max.max(new Vector3(position.x, position.y, position.z));
            });

            return min.add(max).multiplyScalar(0.5);
        }, [centered, solution]);

        const totalParts = useMemo(
            () =>
                solution?.placements.reduce(
                    (total, group) =>
                        total + group.instances.reduce((count, instance) => count + instance.parts.length, 0),
                    0
                ) ?? 0,
            [solution]
        );

        useEffect(() => {
            if (!isPlacementPreviewContext || totalParts === 0) {
                onLoadingChange?.(false);
                return;
            }

            if (loadedPartsRef.current.solutionKey !== solutionKey) {
                loadedPartsRef.current = { solutionKey, count: 0 };
            }

            onLoadingChange?.(loadedPartsRef.current.count < totalParts);
        }, [isPlacementPreviewContext, onLoadingChange, solutionKey, totalParts]);

        const handleOnLoaded = useCallback(() => {
            if (onBoundsReady && groupRef.current) {
                onBoundsReady(groupRef.current);
            }
            if (loadedPartsRef.current.solutionKey !== solutionKey) {
                loadedPartsRef.current = { solutionKey, count: 0 };
            }
            loadedPartsRef.current.count += 1;
            if (loadedPartsRef.current.count >= totalParts) {
                onLoadingChange?.(false);
            }
        }, [onBoundsReady, onLoadingChange, solutionKey, totalParts]);

        if (!solution) {
            return null;
        }

        return (
            <group
                key={solutionKey}
                ref={groupRef}
                position={[-localCenter.x, -localCenter.y, -localCenter.z]}
                visible={isVisible}
            >
                {solution.placements.map((group) =>
                    group.instances.map((instance) => (
                        <PreviewInstance
                            key={`${solutionKey}-${instance.id}`}
                            articleId={group.articleGuid}
                            articleNumber={group.articleNumber}
                            instance={instance}
                            useDefaultMaterial={!isMainCanvas}
                            onLoaded={handleOnLoaded}
                        />
                    ))
                )}
            </group>
        );
    }
);
PlacementPreview.displayName = 'PlacementPreview';
